import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Tooltip } from 'antd';

import { keyBy, keys, maxBy, values } from 'lodash';

export const Axis = (props: any) => {
    const svgRef = useRef();

    const {
        likelihoods = [],
        impacts = [],
        impactLabel = 'Impact →',
        likelihoodLabel = 'Likelihood →'
    } = props;

    const maxLikelihood = likelihoods && parseInt(maxBy(values(likelihoods), 'id')?.id || 3);
    const maxImpact = impacts && parseInt(maxBy(values(impacts), 'id')?.id || 3);

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        if (!svg.node()) return;

        svg.selectAll('g.risk-scatter-axis-layer').remove();
        const layer = svg
            .insert('g', ':first-child')
            .attr('class', 'risk-scatter-axis-layer');

        // Grille légère : lignes verticales/horizontales aux valeurs entières
        // (1..max) d'impact et de probabilité. Posée en premier dans le layer
        // (donc derrière les ticks et les points). Teinte très faible pour
        // rester discrète sur le dégradé.
        const grid = layer.append('g').attr('class', 'grid');
        // Bornée exactement à la boîte de données [1..max] (pas de débordement
        // au-delà des ticks via chartMargin). Trait fin et discret.
        const gridTop = props.y(maxLikelihood);
        const gridBottom = props.y(1);
        const gridLeft = props.x(1);
        const gridRight = props.x(maxImpact);
        // Couleur/opacité pilotées en CSS (.grid-line) plutôt qu'en inline : les
        // lignes ne sont (re)dessinées qu'au montage de l'Axis, donc un réglage
        // inline ne s'appliquerait pas en HMR ; via CSS, les <line> existantes
        // prennent la nouvelle valeur en direct.
        for (let i = 1; i <= maxImpact; i++) {
            grid.append('line').attr('class', 'grid-line')
                .attr('x1', props.x(i)).attr('x2', props.x(i))
                .attr('y1', gridTop).attr('y2', gridBottom);
        }
        for (let j = 1; j <= maxLikelihood; j++) {
            grid.append('line').attr('class', 'grid-line')
                .attr('x1', gridLeft).attr('x2', gridRight)
                .attr('y1', props.y(j)).attr('y2', props.y(j));
        }

        // Add X axis - without axis line
        let xAxisGenerator = d3.axisBottom(props.x);
        xAxisGenerator.tickSize(0)
        xAxisGenerator.ticks(maxImpact)

        // Add the axis label (couleur en CSS .axis-label → tunable + visible)
        layer.append("text")
            .attr("class", "axis-label")
            .attr("x", props.inverted ? props.x(maxLikelihood - 0.5) : props.x(1.5))
            .attr("y", `${props.height - 10}`)
            .style("text-anchor", "middle")
            .style("font-size", "12px")
            .text(impactLabel)


        layer.append("g")
            .attr("transform", `translate(0, ${props.height - 21})`)
            .call(xAxisGenerator)
            .select("path, line")
            .remove();

        let yAxisGenerator = (props.inverted ? d3.axisRight : d3.axisLeft)(props.y);
        yAxisGenerator.tickSize(0);
        yAxisGenerator.ticks(maxLikelihood);

        // Chiffres de l'axe Y, décalés vers l'intérieur pour ne pas croiser le
        // titre d'axe (qui reste collé au bord). Couleur en CSS (.tick text).
        layer.append("g")
            .attr("transform", `translate(${props.inverted ? props.width - 36 : 36}, 0)`)
            .call(yAxisGenerator)
            .select("path, line")
            .remove();

        // NB : pas de .style('stroke') global ici — il repeindrait les lignes de
        // grille (.grid-line) en inline et écraserait leur style CSS.

        // Titre de l'axe Y, collé au bord (x≈6) pour rester à l'écart des
        // chiffres. Couleur en CSS (.axis-label) → lisible en thème clair/sombre.
        layer.append("g")
            .attr("transform", `translate(${props.inverted ? props.width - 13 : 13}, ${props.y((1 + maxLikelihood) / 2)})`)
            .append("text")
            .attr("class", "axis-label")
            .attr("transform", "rotate(-90)")
            .style("text-anchor", "middle")
            .style("font-size", "12px")
            .text(likelihoodLabel)

        /* svg.append("circle")
            .attr("cx", props.x(1 - props.chartMargin))
            .attr("cy", props.y(1 - props.chartMargin))
            .attr("r", 2)
            .style("fill", "white");

        svg.append("circle")
            .attr("cx", props.x(1))
            .attr("cy", props.y(1))
            .attr("r", 2)
            .style("fill", "grey");

        svg.append("circle")
            .attr("cx", props.x(maxImpact))
            .attr("cy", props.y(maxLikelihood))
            .attr("r", 2)
            .style("fill", "grey");

        svg.append("circle")
            .attr("cx", props.x(maxImpact + props.chartMargin))
            .attr("cy", props.y(maxLikelihood + props.chartMargin))
            .attr("r", 2)
            .style("fill", "grey");

        svg.append("circle")
            .attr("cx", props.x(1))
            .attr("cy", props.y(maxLikelihood))
            .attr("r", 2)
            .style("fill", "grey");

        svg.append("circle")
            .attr("cx", props.x(1 - props.chartMargin))
            .attr("cy", props.y(maxLikelihood + props.chartMargin))
            .attr("r", 2)
            .style("fill", "grey");

        svg.append("circle")
            .attr("cx", props.x(maxImpact))
            .attr("cy", props.y(1))
            .attr("r", 2)
            .style("fill", "grey");

        svg.append("circle")
            .attr("cx", props.x(maxImpact + props.chartMargin))
            .attr("cy", props.y(1 - props.chartMargin))
            .attr("r", 2)
            .style("fill", "grey"); */
        // Intentionally []: x/y scales are new object refs each parent render; parent
        // should change `key` (e.g. include i18n.language) when labels or geometry must refresh.
    }, []);

    return (
        <svg className="axes" ref={svgRef}>{props.children}</svg>
    );
};
